import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { CardXpProgressBar } from '@sorare/marketplace/src/components/card/levelup/CardXpProgressBar';
import { LevelUpAnimation } from '@sorare/marketplace/src/components/card/levelup/LevelUpAnimation';
import { useCardXpProgressBarSprings } from '@sorare/marketplace/src/components/card/levelup/useCardXpProgressBarSprings';

import type {
  LevelUp_cardXpGradeThreshold,
  LevelUp_leveledUpCard,
} from './__generated__/index.graphql';

const ProgressBarContainer = styled.div`
  padding: var(--double-unit);
`;

type Props = {
  cardLeveledUp: LevelUp_leveledUpCard;
  gradeThresholds?: LevelUp_cardXpGradeThreshold[];
  focused?: boolean;
  delay?: number;
};

export const LevelUp = ({
  cardLeveledUp,
  gradeThresholds,
  focused,
  delay,
}: Props) => {
  const { level, percentage, backgroundColor, color, borderColor } =
    useCardXpProgressBarSprings({
      cardLeveledUp,
      gradeThresholds,
      started: focused,
      delay,
    });

  return (
    <Vertical>
      <LevelUpAnimation animate={focused} delay={delay}>
        {cardLeveledUp.card.pictureUrl && (
          <img src={cardLeveledUp.card.pictureUrl} alt="" />
        )}
      </LevelUpAnimation>
      <ProgressBarContainer>
        <CardXpProgressBar
          level={level}
          percentage={percentage}
          backgroundColor={backgroundColor}
          color={color}
          borderColor={borderColor}
        />
      </ProgressBarContainer>
    </Vertical>
  );
};

LevelUp.fragments = {
  cardXpGradeThreshold: gql`
    fragment LevelUp_cardXpGradeThreshold on CardXPGradeThreshold {
      ...useCardXpProgressBarSprings_cardXpGradeThreshold
    }
    ${useCardXpProgressBarSprings.fragments.cardXpGradeThreshold}
  ` as TypedDocumentNode<LevelUp_cardXpGradeThreshold>,
  leveledUpCard: gql`
    fragment LevelUp_leveledUpCard on LeveledUpCard {
      id
      card {
        slug
        pictureUrl
      }
      ...useCardXpProgressBarSprings_leveledUpCard
    }
    ${useCardXpProgressBarSprings.fragments.leveledUpCard}
  ` as TypedDocumentNode<LevelUp_leveledUpCard>,
};
